import React, { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Save, PoundSterling } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const fmtGbp = (v) =>
  v == null ? "—" : `£${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function ActualIncomePanel({ projectId, project }) {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "manager";
  const rentalised = !!project?.is_rentalised;
  const fit = !!project?.fit_export;
  // Active money fields for this site's commercial model.
  const FIELDS = [
    ...(rentalised ? [{ k: "rental_income_gbp", label: "Rental £", testid: "rental" }]
                   : [{ k: "billing_income_gbp", label: "Billing £", testid: "billing" },
                      { k: "export_income_gbp", label: "Export £", testid: "export" }]),
    ...(fit ? [{ k: "fit_income_gbp", label: "FIT £", testid: "fit" }] : []),
    { k: "opex_gbp", label: "OPEX £", testid: "opex", nullable: true },
  ];
  const emptyDraft = () => {
    const d = { month: thisMonth() };
    FIELDS.forEach(f => { d[f.k] = ""; });
    return d;
  };
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ month: thisMonth(), billing_income_gbp: "", export_income_gbp: "", rental_income_gbp: "", fit_income_gbp: "", opex_gbp: "" });
  const [saving, setSaving] = useState(false);
  const [vsModel, setVsModel] = useState(null);

  const loadVsModel = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/income/vs-model`);
      setVsModel(data);
    } catch { setVsModel(null); }
  }, [projectId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/income`);
      setRows(data.income || []);
      setTotals(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); loadVsModel(); }, [load, loadVsModel]);

  const save = async () => {
    if (!draft.month) { toast.error("Pick a month"); return; }
    setSaving(true);
    try {
      const payload = { month: draft.month };
      FIELDS.forEach(f => {
        payload[f.k] = f.nullable
          ? (draft[f.k] === "" ? null : Number(draft[f.k]))
          : (Number(draft[f.k]) || 0);
      });
      await api.post(`/projects/${projectId}/income`, payload);
      toast.success(`Saved ${draft.month}`);
      setDraft(emptyDraft());
      load(); loadVsModel();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (month) => {
    try {
      await api.delete(`/projects/${projectId}/income/${month}`);
      toast.success(`Removed ${month}`);
      load(); loadVsModel();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const editRow = (r) => {
    const d = { month: r.month };
    FIELDS.forEach(f => { d[f.k] = r[f.k] ?? ""; });
    setDraft(d);
  };
  const rowTotal = (r) => FIELDS.filter(f => !f.nullable)
    .reduce((a, f) => a + (Number(r[f.k]) || 0), 0);

  return (
    <section className="swiss-card-flat p-6" data-testid="actual-income-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="label flex items-center gap-1.5"><PoundSterling size={14} /> Actual monthly income</div>
          <div className="text-xs muted mt-1">
            {rentalised ? "Flat rental income" : "Billing + export cash"}
            {fit ? " + FIT payments" : ""} — the basis for Yield-on-Cost &amp; IRR tracking
          </div>
          {(rentalised || fit) && (
            <div className="flex gap-2 mt-2" data-testid="income-model-badges">
              {rentalised && <span className="px-2 py-0.5 rounded-full text-[10px] bg-[var(--accent-soft,#DDF3F1)] text-[var(--accent-hover)] font-semibold">RENTALISED</span>}
              {fit && (
                <span className="px-2 py-0.5 rounded-full text-[10px] bg-amber-100 text-amber-800 font-semibold"
                      data-testid="income-fit-badge">
                  FIT{project?.fit_provider ? ` · ${project.fit_provider}` : ""}
                  {project?.fit_reference ? ` · ${project.fit_reference}` : ""}
                  {project?.fit_frequency_months ? ` · every ${project.fit_frequency_months}m` : ""}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="label">Recorded total</div>
          <div className="font-mono text-lg font-bold" data-testid="income-total">{fmtGbp(totals.total_income_gbp)}</div>
        </div>
      </div>

      {canEdit && (
        <div className="flex flex-wrap gap-2 items-end mb-4 p-3 bg-[var(--surface-2)] border border-[var(--line)]"
             data-testid="income-entry-row">
          <label className="block w-36">
            <span className="label block mb-1">Month</span>
            <input type="month" className="input-swiss w-full" value={draft.month}
                   max={thisMonth()} onChange={(e) => setDraft({ ...draft, month: e.target.value })}
                   data-testid="income-month-input" />
          </label>
          {FIELDS.map(f => (
            <label className="block w-32" key={f.k}>
              <span className="label block mb-1">{f.label}{f.nullable ? " (opt)" : ""}</span>
              <input type="number" step="0.01" className="input-swiss w-full" value={draft[f.k]}
                     placeholder={f.nullable ? "modelled" : ""}
                     onChange={(e) => setDraft({ ...draft, [f.k]: e.target.value })}
                     data-testid={`income-${f.testid}-input`} />
            </label>
          ))}
          <button onClick={save} disabled={saving} className="btn-primary justify-center"
                  data-testid="income-save-btn">
            {saving ? "Saving…" : <><Plus size={13} /> Save month</>}
          </button>
        </div>
      )}

      {loading ? (
        <div className="muted text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="muted text-sm" data-testid="income-empty">No income recorded yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="income-table">
            <thead>
              <tr className="text-left label border-b border-[var(--line)]">
                <th className="py-2 pr-3">Month</th>
                {FIELDS.map(f => <th key={f.k} className="py-2 pr-3 text-right">{f.label}</th>)}
                <th className="py-2 pr-3 text-right">Total £</th>
                {canEdit && <th className="py-2" />}
              </tr>
            </thead>
            <tbody className="font-mono text-xs">
              {rows.map((r) => (
                <tr key={r.month} className="border-b border-[var(--line)] hover:bg-[var(--surface-2)]"
                    data-testid={`income-row-${r.month}`}>
                  <td className="py-2 pr-3">{r.month}</td>
                  {FIELDS.map(f => (
                    <td key={f.k} className={`py-2 pr-3 text-right ${f.nullable ? "muted" : ""}`}>
                      {f.nullable ? (r[f.k] == null ? "—" : fmtGbp(r[f.k])) : fmtGbp(r[f.k])}
                    </td>
                  ))}
                  <td className="py-2 pr-3 text-right font-bold">{fmtGbp(rowTotal(r))}</td>
                  {canEdit && (
                    <td className="py-2 text-right whitespace-nowrap">
                      <button onClick={() => editRow(r)} className="btn-ghost text-[10px] mr-1"
                              data-testid={`income-edit-${r.month}`}>Edit</button>
                      <button onClick={() => remove(r.month)} className="btn-ghost text-[10px] text-red-600"
                              data-testid={`income-delete-${r.month}`}><Trash2 size={11} /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {vsModel?.has_model && (vsModel.series || []).length > 0 && (
        <div className="mt-6 pt-6 border-t border-[var(--line)]" data-testid="income-vs-model-chart">
          <div className="label mb-3">Recorded income vs modelled (design plan)</div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer width="99%" height="100%" minWidth={1} minHeight={1}>
              <ComposedChart data={vsModel.series} margin={{ top: 10, right: 10, bottom: 4, left: 0 }}>
                <CartesianGrid stroke="#E4E4E7" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 9, fontFamily: "IBM Plex Mono" }} stroke="#A1A1AA" />
                <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#A1A1AA"
                       tickFormatter={(v) => (v >= 1000 ? `£${(v / 1000).toFixed(0)}k` : `£${v}`)} />
                <Tooltip contentStyle={{ borderRadius: 0, fontSize: 12 }}
                         formatter={(v) => `£${Number(v).toLocaleString()}`} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="actual_income_gbp" name="Actual income" fill="var(--accent)" radius={[2, 2, 0, 0]} />
                <Line dataKey="modelled_income_gbp" name="Modelled (design)" stroke="#52525B"
                      strokeWidth={2} strokeDasharray="5 3" dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </section>
  );
}
